import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { EmptyTableMessage } from "@/core/components/empty-table-message";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { SearchInput } from "@/core/components/search-input";
import { SimpleTable } from "@/core/components/simple-table";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import {
  TimeRangePicker,
  TimeRange,
  TimeRangePreset,
} from "@/core/components/ui/time-range-picker";
import { useBusiness } from "@/core/business";
import { formatDate } from "@/core/utils/date";
import { downloadCSV } from "@/core/utils/download-csv";
import { cn } from "@/core/utils/utils";
import { DetailedUserWithStats } from "@/user/user.d";
import { ClosedReferralsCol } from "./cols/closed-referrals-col";
import { EmailCol } from "./cols/email-col";
import { LastReferralDateCol } from "./cols/last-referral-date-col";
import { NameCol } from "./cols/name-col";
import { RankingCol } from "./cols/ranking-col";
import { ReferralCodeCol } from "./cols/referral-code-col";
import { SignupsCol } from "./cols/signups-col";
import { TotalReferralsCol } from "./cols/total-referrals-col";

const CSV_HEADERS = [
  [
    "Rank",
    "Name",
    "Referral Code",
    "Signups",
    "Total Referrals",
    "Closed Referrals",
    "Last Referral",
  ],
];

const referralsCols = [
  RankingCol(),
  NameCol(),
  ReferralCodeCol(),
  SignupsCol(),
  TotalReferralsCol(),
  ClosedReferralsCol(),
  LastReferralDateCol(),
];

const spiffOnlyCols = [NameCol(), EmailCol()];

interface TechniciansTableProps {
  data: DetailedUserWithStats[];
  timeRange?: TimeRange | null;
  setTimeRange?: Dispatch<SetStateAction<TimeRange | null>>;
  presets?: TimeRangePreset[];
  className?: string;
  isLoading?: boolean;
  isLoadingStats?: boolean;
  onRowClick?: (row: DetailedUserWithStats) => void;
  selectedTechnicianId?: string;
}

export const TechniciansTable = ({
  data,
  timeRange,
  setTimeRange,
  presets,
  isLoading,
  isLoadingStats,
  className,
  onRowClick,
  selectedTechnicianId,
}: TechniciansTableProps) => {
  const { isSpiffOnly } = useBusiness();
  const [searchValue, setSearchValue] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);

  const columns = useMemo(
    () => (isSpiffOnly ? spiffOnlyCols : referralsCols),
    [isSpiffOnly],
  );

  const handleDownloadCSV = async () => {
    setIsDownloading(true);
    try {
      const csvData = [CSV_HEADERS[0]];
      data.forEach((row, index) => {
        csvData.push([
          (index + 1).toString(),
          `${row.first_name} ${row.last_name}`,
          row.referral_code ? `Referral${row.referral_code}` : "",
          (row.stats?.signup_count || 0).toString(),
          (row.stats?.total_referral_count || 0).toString(),
          (row.stats?.closed_referral_count || 0).toString(),
          row.stats?.last_referral_date
            ? formatDate(row.stats.last_referral_date)
            : "",
        ]);
      });
      const filename = timeRange
        ? `technicians-report-${timeRange.from.toISOString().split("T")[0]}-to-${timeRange.to.toISOString().split("T")[0]}.csv`
        : "technicians-report.csv";
      downloadCSV(csvData, filename);
    } catch (error) {
      console.error("Error generating CSV:", error);
      // Optionally add error notification here
    } finally {
      setIsDownloading(false);
    }
  };

  if (data.length === 0 && !isLoading) {
    return <EmptyTableMessage />;
  }

  return (
    <Stack
      gap="sm"
      className={cn("flex h-full min-h-0 flex-col", className)}
      fullWidth
    >
      <Stack
        direction="row"
        justify="between"
        align="center"
        className="shrink-0 pr-5"
        fullWidth
      >
        <SearchInput
          value={searchValue}
          onChange={setSearchValue}
          placeholder="Search Technicians"
        />
        {!isSpiffOnly && (
          <Stack direction="row" align="center" gap="xs">
            {isLoadingStats && <LoadingSpinner size="sm" color="secondary" />}
            <TimeRangePicker
              value={timeRange}
              onChange={setTimeRange}
              placeholder="Select date range"
              presets={presets}
            />
            <Button
              variant="text"
              size="sm"
              onClick={handleDownloadCSV}
              disabled={isLoading || isLoadingStats}
              loading={isDownloading}
              leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
            >
              Download report
            </Button>
          </Stack>
        )}
      </Stack>
      <SimpleTable
        columns={columns}
        data={data}
        isLoading={isLoading}
        className="min-h-0 w-full flex-1 overflow-hidden"
        onRowClick={onRowClick}
        selectedRowId={selectedTechnicianId}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        tableClassName="min-w-[1000px] mb-2"
      />
    </Stack>
  );
};
